import {AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {MergedBudgetListService} from './merged-budget-list.service';
import {Observable, Subject} from 'rxjs';
import {Budget} from '../../interfaces';
import {MatPaginator, MatPaginatorModule} from '@angular/material/paginator';
import {MatSort, MatSortHeader, SortDirection} from '@angular/material/sort';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {filter, first, takeUntil} from 'rxjs/operators';
import {BudgetListFilter, BudgetListTableService} from '../../services/budget-list-table.service';
import {ProjectNrPipe} from '../../shared/pipes/project-nr.pipe';
import {DataService} from '../../services/data.service';
import {Router} from '@angular/router';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';
import {CommonModule} from '@angular/common';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';

@Component({
    selector: 'app-merged-budget-list',
    templateUrl: './merged-budget-list.component.html',
    styleUrls: ['./merged-budget-list.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        MatProgressSpinnerModule,
        MatTableModule,
        MatSort,
        MatSortHeader,
        MatPaginatorModule,
        MatSnackBarModule
    ],
    providers: [MergedBudgetListService, ProjectNrPipe]
})
export class MergedBudgetListComponent implements OnInit, AfterViewInit, OnDestroy {

    public isLoading$: Observable<boolean>;
    public displayedColumns: string[] = ['budgetNr', 'budgetName', 'link'];
    public dataSource = new MatTableDataSource<Budget>([]);
    private unsubscribe$ = new Subject<void>();
    private appliedFilter: BudgetListFilter;

    @Output() updateTotalElements: EventEmitter<number> = new EventEmitter<number>();

    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
    @ViewChild(MatSort, {static: true}) sort: MatSort;

    constructor(
        private mergedBudgetListService: MergedBudgetListService,
        private budgetListTableService: BudgetListTableService,
        private projectNrPipe: ProjectNrPipe,
        private dataService: DataService,
        private router: Router,
        private snackBar: MatSnackBar
    ) {
        this.isLoading$ = this.mergedBudgetListService.isLoading$;
    }

    ngOnInit() {
        this.mergedBudgetListService.budgets$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(budgets => {
                this.dataSource.data = budgets ?? [];
                this.updateTotalElements.emit(this.dataSource.filteredData.length);
            });

        this.budgetListTableService.appliedFilterAndSortingAndPagination$.pipe(
            filter(newFilter => this.budgetListTableService.compareFilters(newFilter, this.appliedFilter)),
            takeUntil(this.unsubscribe$)
        ).subscribe(inputFilter => this.handleFilterChange(inputFilter));
    }

    ngAfterViewInit() {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.dataSource.filterPredicate = this.quickTableFilterPredicate();

        this.sort.sortChange
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                this.paginator.pageIndex = 0;
                this.budgetListTableService.storeSortingAndPageSize(this.sort.active, this.sort.direction, this.paginator.pageSize);
            });

        this.paginator.page
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                this.budgetListTableService.storeSortingAndPageSize(this.sort.active, this.sort.direction, this.paginator.pageSize);
            });
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    goToNewProject(mergedBudgetId: string) {
        this.dataService.getOpportunityByMergedBudgetId(mergedBudgetId)
            .pipe(first())
            .subscribe((opportunities: any[]) => {
                if (opportunities.length > 0) {
                    const budgetId = opportunities[0].budgetId;
                    this.router.navigate(['/budget', budgetId]);
                } else {
                    this.snackBar.open('Opportunity not found for this merged budget.', 'Close', {
                        duration: 5000
                    });
                }
            });
    }

    private handleFilterChange(inputFilter: BudgetListFilter) {
        this.restoreSorting(inputFilter);
        this.restorePageSize(inputFilter);
        this.appliedFilter = {...inputFilter};
        this.paginator.pageIndex = 0;
        this.dataSource.filter = JSON.stringify(inputFilter);
        this.updateTotalElements.emit(this.dataSource.filteredData.length);
    }

    private restoreSorting(inputFilter: BudgetListFilter) {
        const needRefresh = this.sort.active !== inputFilter.sortActive || this.sort.direction !== inputFilter.sortDirection;
        this.sort.active = (inputFilter.sortActive) ? inputFilter.sortActive : 'creation';
        this.sort.direction = (inputFilter.sortDirection) ? inputFilter.sortDirection as SortDirection : 'desc';
        if (needRefresh) {
            this.sort.sortChange.emit();
        }
    }

    private restorePageSize(inputFilter: BudgetListFilter) {
        const needRefresh = this.paginator.pageSize !== inputFilter.pageSize;
        this.paginator.pageSize = (inputFilter.pageSize) ? inputFilter.pageSize : 10;
        if (needRefresh) {
            this.paginator.page.emit();
        }
    }

    private quickTableFilterPredicate(): (budget: Budget, filterValue: string) => boolean {
        return (budget: Budget, inputFilter: string) => {
            if (!inputFilter) {
                return true;
            }
            const filterValue = JSON.parse(inputFilter);

            const dateFrom = filterValue.dateFrom ? new Date(filterValue.dateFrom) : null;
            const dateTo = filterValue.dateTo ? new Date(filterValue.dateTo) : null;

            const budgetCreation = new Date(budget.creation);
            if (dateFrom && budgetCreation <= dateFrom) {
                return false;
            }
            if (dateTo && budgetCreation >= dateTo) {
                return false;
            }

            let searchStr = budget.budgetNr || '';
            if (budget.project) {
                searchStr += this.projectNrPipe.transform(budget);
            }
            if (budget.company?.companyName) {
                searchStr += budget.company.companyName;
            }
            if (budget.project?.projectManager) {
                searchStr += budget.project.projectManager.displayName;
            }
            if (budget.assignee) {
                searchStr += budget.assignee.displayName;
            }
            if (budget.budgetName) {
                searchStr += budget.budgetName;
            }
            if (budget.author) {
                searchStr += budget.author.displayName;
            } else {
                searchStr += budget.description;
            }

            searchStr = searchStr.toLowerCase();

            let iconSearchStr = [undefined, null, ''].includes(budget.status) ? 'default' : budget.status;
            iconSearchStr = iconSearchStr.toLowerCase();

            const filterCondition = (filterValue.filter) ? searchStr.indexOf(filterValue.filter.toLowerCase()) !== -1 : true;
            const statusCondition = (filterValue.status) ? iconSearchStr.includes(filterValue.status.toLowerCase()) : true;
            return (filterCondition && statusCondition) && (filterValue.showExport || (!budget.export));
        };
    }
}